import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dices, RotateCcw } from 'lucide-react';
import { StudentAvatar } from '../components/StudentAvatar';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const SPOTLIGHT_COLORS = [
  '#4338ca', '#059669', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#ea580c', '#0d9488', '#db2777', '#2563eb',
];

export const StudentPickerPage = () => {
  const { classId } = useParams();
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [picking, setPicking] = useState(false);
  const [pickCount, setPickCount] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [highlightedIds, setHighlightedIds] = useState(new Set());
  const intervalRefs = useRef([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentsRes, classesRes] = await Promise.all([
          fetch(`${API}/students/${classId}`, { credentials: 'include' }),
          fetch(`${API}/classes`, { credentials: 'include' }),
        ]);
        if (studentsRes.ok) setStudents(await studentsRes.json());
        if (classesRes.ok) {
          const classes = await classesRes.json();
          setClassInfo(classes.find(c => c.class_id === classId));
        }
      } catch (error) { console.error('Failed to fetch data:', error); }
    };
    fetchData();
  }, [classId]);

  const pick = useCallback(() => {
    if (picking || students.length === 0) return;
    const count = Math.min(pickCount, students.length, 10);
    setPicking(true);
    setSelectedIds(new Set());
    setHighlightedIds(new Set());

    // For each spotlight, run an independent roaming animation
    const totalDuration = 3000;
    const staggerDelay = 200; // Stagger start of each spotlight
    const finalWinners = [];

    // Pre-select winners (unique)
    const shuffled = [...students].sort(() => Math.random() - 0.5);
    for (let i = 0; i < count; i++) {
      finalWinners.push(shuffled[i].student_id);
    }

    for (let s = 0; s < count; s++) {
      const startTime = Date.now() + s * staggerDelay;
      let currentIdx = Math.floor(Math.random() * students.length);

      const cycle = () => {
        const elapsed = Date.now() - startTime;
        if (elapsed < 0) {
          intervalRefs.current[s] = setTimeout(cycle, 50);
          return;
        }
        const progress = Math.min(elapsed / totalDuration, 1);

        currentIdx = (currentIdx + 1) % students.length;
        setHighlightedIds(prev => {
          const next = new Set(prev);
          // Remove previous highlight for this spotlight
          next.delete(`spot_${s}`);
          // Add current
          next.add(students[currentIdx].student_id);
          return next;
        });

        if (progress >= 1) {
          // Settle on final winner
          const winnerId = finalWinners[s];
          setHighlightedIds(prev => {
            const next = new Set(prev);
            next.add(winnerId);
            return next;
          });
          setSelectedIds(prev => {
            const next = new Set(prev);
            next.add(winnerId);
            // If all spotlights done
            if (next.size >= count) {
              setTimeout(() => setPicking(false), 100);
            }
            return next;
          });
          return;
        }

        const currentInterval = 50 + 250 * Math.pow(progress, 2);
        intervalRefs.current[s] = setTimeout(cycle, currentInterval);
      };

      cycle();
    }
  }, [picking, students, pickCount]);

  useEffect(() => {
    return () => {
      intervalRefs.current.forEach(ref => { if (ref) clearTimeout(ref); });
    };
  }, []);

  const reset = () => {
    setSelectedIds(new Set());
    setHighlightedIds(new Set());
    setPicking(false);
    intervalRefs.current.forEach(ref => { if (ref) clearTimeout(ref); });
  };

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: classInfo?.name || 'Class', path: '/app/live-mode' },
    { label: 'Student Picker' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="student-picker-page">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-violet-50 mb-4">
            <Dices className="w-7 h-7 text-violet-600" />
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Random Student Selector</h1>
          <p className="text-slate-500 mt-1 text-sm">{classInfo?.name} &middot; {students.length} students</p>
        </div>

        {/* Controls */}
        <Card className="bg-white border border-slate-200 rounded-xl shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-4 justify-center flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600 whitespace-nowrap">Pick</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={pickCount}
                  onChange={(e) => setPickCount(Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-16 h-10 text-center border-slate-200 rounded-xl font-bold"
                  data-testid="pick-count-input"
                />
                <label className="text-sm font-medium text-slate-600 whitespace-nowrap">
                  student{pickCount > 1 ? 's' : ''}
                </label>
              </div>
              <Button
                onClick={pick}
                disabled={picking || students.length === 0}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-10 px-6"
                data-testid="spotlight-btn"
              >
                <Dices className="w-5 h-5 mr-2" />{picking ? 'Picking...' : 'Spotlight!'}
              </Button>
              {selectedIds.size > 0 && !picking && (
                <Button variant="outline" onClick={reset} className="border-slate-200 rounded-xl h-10" data-testid="reset-btn">
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Student grid — all names visible */}
        {students.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="student-grid">
            {students.map((student, index) => {
              const isSelected = selectedIds.has(student.student_id);
              const isHighlighted = highlightedIds.has(student.student_id) && picking;
              const color = student.avatar_color || SPOTLIGHT_COLORS[index % SPOTLIGHT_COLORS.length];

              return (
                <div
                  key={student.student_id}
                  className={`relative flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-150 ${
                    isSelected
                      ? 'border-amber-400 bg-amber-50 shadow-lg scale-105 ring-2 ring-amber-200'
                      : isHighlighted
                        ? 'border-teal-400 bg-teal-50 shadow-md scale-102'
                        : 'border-slate-100 bg-white hover:border-slate-200'
                  }`}
                  data-testid={`student-tile-${student.student_id}`}
                >
                  {/* Spotlight glow effect */}
                  {(isSelected || isHighlighted) && (
                    <div
                      className="absolute inset-0 rounded-xl pointer-events-none"
                      style={{
                        background: isSelected
                          ? `radial-gradient(circle at center, ${color}20 0%, transparent 70%)`
                          : `radial-gradient(circle at center, ${color}15 0%, transparent 70%)`,
                      }}
                    />
                  )}
                  <StudentAvatar student={student} size="md"
                    className={`mb-2 ${isSelected ? 'ring-4 ring-amber-300 scale-110' : isHighlighted ? 'ring-2 ring-teal-300' : ''}`} />
                  <span className={`text-xs font-medium text-center leading-tight ${
                    isSelected ? 'text-amber-800 font-semibold' : 'text-slate-600'
                  }`}>
                    {student.name}
                  </span>
                  {isSelected && (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow" data-testid={`selected-badge-${student.student_id}`}>
                      ★
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <Card className="bg-white border border-dashed border-slate-300 shadow-none">
            <CardContent className="py-12 text-center">
              <Dices className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-700 mb-1">No students in this class</h3>
              <p className="text-sm text-slate-400">Add students first</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};
